import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Smartphone, Landmark } from "lucide-react";
import api from "../api/axios";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";

export default function Checkout() {
  const { cart, total, clearCart } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [address, setAddress] = useState({ line1: "", city: "", state: "", postalCode: "", country: "India", phone: "" });
  const [method, setMethod] = useState("razorpay"); // "razorpay" (UPI/card/netbanking) or "cod"
  const [placing, setPlacing] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (e) => setAddress({ ...address, [e.target.name]: e.target.value });
  const items = () => cart.map((i) => ({ product: i._id, qty: i.qty }));

  const placeCodOrder = async () => {
    const { data } = await api.post("/orders", {
      items: items(),
      shippingAddress: address,
      paymentMethod: "cod",
    });
    return data;
  };

  const placeRazorpayOrder = async () => {
    // 1. Ask our backend to create a Razorpay order (amount is computed server-side)
    const { data: rp } = await api.post("/payments/create-razorpay-order", { items: items() });

    // 2. Open the Razorpay checkout widget — this is where the customer picks
    //    UPI / card / netbanking / wallet and completes payment
    return new Promise((resolve, reject) => {
      const rzp = new window.Razorpay({
        key: rp.keyId,
        amount: rp.amount,
        currency: rp.currency,
        name: "GridCase",
        description: "Mobile accessories order",
        order_id: rp.orderId,
        prefill: {
          name: user?.name || "",
          email: user?.email || "",
          contact: address.phone || "",
        },
        theme: { color: "#FF5A36" },
        handler: async (response) => {
          // 3. Payment succeeded — send the signed response to our backend to verify
          //    and create the order. We NEVER mark an order paid on the frontend alone.
          try {
            const { data: order } = await api.post("/orders", {
              items: items(),
              shippingAddress: address,
              paymentMethod: "razorpay",
              razorpayOrderId: response.razorpay_order_id,
              razorpayPaymentId: response.razorpay_payment_id,
              razorpaySignature: response.razorpay_signature,
            });
            resolve(order);
          } catch (err) {
            reject(err);
          }
        },
        modal: {
          ondismiss: () => reject(new Error("Payment cancelled")),
        },
      });
      rzp.on("payment.failed", () => reject(new Error("Payment failed. No amount was deducted if it wasn't completed.")));
      rzp.open();
    });
  };

  const placeOrder = async (e) => {
    e.preventDefault();
    setError("");
    setPlacing(true);
    try {
      const order = method === "razorpay" ? await placeRazorpayOrder() : await placeCodOrder();
      clearCart();
      navigate(`/order/${order._id}`);
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Failed to place order");
    } finally {
      setPlacing(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-12">
      <h1 className="font-display font-semibold text-2xl mb-8">Checkout</h1>
      <form onSubmit={placeOrder} className="space-y-4">
        <input name="line1" value={address.line1} onChange={handleChange} placeholder="Address line" required
          className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-sm outline-none focus:border-accent/60" />
        <div className="grid grid-cols-2 gap-4">
          <input name="city" value={address.city} onChange={handleChange} placeholder="City" required
            className="bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-sm outline-none focus:border-accent/60" />
          <input name="state" value={address.state} onChange={handleChange} placeholder="State" required
            className="bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-sm outline-none focus:border-accent/60" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <input name="postalCode" value={address.postalCode} onChange={handleChange} placeholder="PIN code" required
            className="bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-sm outline-none focus:border-accent/60" />
          <input name="phone" value={address.phone} onChange={handleChange} placeholder="Phone number" required
            className="bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-sm outline-none focus:border-accent/60" />
        </div>
        <input name="country" value={address.country} onChange={handleChange} placeholder="Country" required
          className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-sm outline-none focus:border-accent/60" />

        <div className="pt-2">
          <div className="text-sm text-white/60 mb-3">Payment method</div>
          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={() => setMethod("razorpay")}
              className={`flex items-center gap-3 rounded-xl border p-4 text-left transition ${
                method === "razorpay" ? "border-accent bg-accent/10" : "border-white/10 bg-white/5"
              }`}
            >
              <Smartphone size={20} className={method === "razorpay" ? "text-accent" : "text-white/50"} />
              <div>
                <div className="text-sm font-medium">UPI / Card / Netbanking</div>
                <div className="text-xs text-white/40">Pay instantly via Razorpay</div>
              </div>
            </button>
            <button
              type="button"
              onClick={() => setMethod("cod")}
              className={`flex items-center gap-3 rounded-xl border p-4 text-left transition ${
                method === "cod" ? "border-accent bg-accent/10" : "border-white/10 bg-white/5"
              }`}
            >
              <Landmark size={20} className={method === "cod" ? "text-accent" : "text-white/50"} />
              <div>
                <div className="text-sm font-medium">Cash on Delivery</div>
                <div className="text-xs text-white/40">Pay when it arrives</div>
              </div>
            </button>
          </div>
        </div>

        <div className="flex justify-between font-mono text-lg border-t border-white/10 pt-4">
          <span>Total</span><span>₹{total.toFixed(2)}</span>
        </div>

        {error && <p className="text-red-400 text-sm">{error}</p>}

        <button
          disabled={placing}
          className="w-full bg-accent hover:bg-orange-500 text-black font-semibold rounded-full py-3 text-sm transition disabled:opacity-50"
        >
          {placing ? "Processing..." : method === "razorpay" ? "Pay now" : "Place order (Cash on delivery)"}
        </button>
      </form>
    </div>
  );
}
